import axios, { AxiosRequestConfig } from "axios";
import { apiUrl } from "./env";
import { getCookieByValue } from "./helpers";
import { refreshAccessToken } from "../actions/authActions";

const http = axios.create({
  baseURL: apiUrl,
  headers: {
    "Content-type": "application/json"
  },
  withCredentials: true,
  // Ensure cookies are sent with cross-domain requests
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

http.interceptors.request.use((config) => {
  // Check if the user is authenticated via session cookie
  const isAuthenticated = getCookieByValue("authenticated");
  const userId = getCookieByValue("user_id");
  
  console.log('HTTP Request:', {
    url: config.url,
    authenticated: isAuthenticated === 'true',
    userId: userId || 'not found',
    existingAuth: !!config.headers.Authorization
  });
  
  // For all requests except login, add authentication headers
  if (!config.url?.includes('authentication/login')) {
    // Set a dummy Authorization header for compatibility
    if (isAuthenticated === 'true' && !config.headers.Authorization) {
      config.headers.Authorization = 'Bearer session-auth';
    }
    
    // Add user ID as a custom header for additional authentication method
    if (userId) {
      config.headers['X-User-ID'] = userId;
    }
  }
  
  return config;
}, (error) => {
  console.error('HTTP Request Error:', error);
  return Promise.reject(error);
});

// Define the structure of a retry queue item
interface RetryQueueItem {
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}

// Create a list to hold the request queue
const refreshAndRetryQueue: RetryQueueItem[] = [];

// Flag to prevent multiple token refresh requests
let isRefreshing = false;

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: AxiosRequestConfig = error.config;

    if (error.response && error.response.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Refresh the access token
          const newAccessToken = await refreshAccessToken();

          // Update the request headers with the new access token
          error.config.headers['Authorization'] = `Bearer ${newAccessToken}`;

          // Retry all requests in the queue with the new token
          refreshAndRetryQueue.forEach(({ config, resolve, reject }) => {
            http
              .request(config)
              .then((response) => resolve(response))
              .catch((err) => reject(err));
          });

          // Clear the queue
          refreshAndRetryQueue.length = 0;

          // Retry the original request
          return http(originalRequest);
        } catch (refreshError) {
          console.error(refreshError)
          throw refreshError;
        } finally {
          isRefreshing = false;
        }
      }

      // Add the original request to the queue
      return new Promise<void>((resolve, reject) => {
        refreshAndRetryQueue.push({ config: originalRequest, resolve, reject });
      });
    }

    // Return a Promise rejection if the status code is not 401
    return Promise.reject(error);
  }
);

export default http
